import { format } from 'date-fns';
import * as Yup from 'yup';

import { validateField, ValidationType } from './formValidation';
import { EARLIEST_BEGIN_DATE } from 'data/constants/constants';
import type { ConsultantLicenseFormInterface, ConsultantLicenseInterface } from 'types';

export const emptyConsultantLicense: ConsultantLicenseInterface = {
    id: null,
    license_number: '',
    country: '',
    date_issued: '',
};

export const licenseFormDefaultValues = (
    licenses?: ConsultantLicenseInterface[]
): ConsultantLicenseFormInterface => ({
    licenses: licenses?.length ? licenses : [{ ...emptyConsultantLicense }],
});

const requiredText = () => (validateField(ValidationType.STRING) as Yup.StringSchema).trim();

const isIssuedDateInRange = (value: string): boolean => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const today = format(new Date(), 'yyyy-MM-dd');
    return value >= EARLIEST_BEGIN_DATE && value <= today;
};

const hasUniqueValue = (
    from: Array<{ value: unknown }> | undefined,
    listKey: string,
    field: string,
    value: unknown
): boolean => {
    if (typeof value !== 'string') return true;
    const current = value.trim().toLowerCase();
    if (!current) return true;

    const entries = from ?? [];
    for (let index = entries.length - 1; index >= 0; index -= 1) {
        const entry = entries[index]?.value;
        if (!entry || typeof entry !== 'object') continue;
        const list = (entry as Record<string, unknown>)[listKey];
        if (!Array.isArray(list)) continue;

        const matches = list.filter((item) => {
            if (!item || typeof item !== 'object') return false;
            const fieldValue = (item as Record<string, unknown>)[field];
            return typeof fieldValue === 'string' && fieldValue.trim().toLowerCase() === current;
        }).length;

        return matches <= 1;
    }

    return true;
};

export const licenseFormValidationSchema = Yup.object({
    licenses: Yup.array()
        .of(
            Yup.object({
                country: requiredText().test(
                    'unique-country',
                    'This country is already added',
                    function (value) {
                        return hasUniqueValue(this.from, 'licenses', 'country', value);
                    }
                ),
                license_number: requiredText().test(
                    'unique-license-number',
                    'This license number is already added',
                    function (value) {
                        return hasUniqueValue(this.from, 'licenses', 'license_number', value);
                    }
                ),
                date_issued: (validateField(ValidationType.DATE) as Yup.StringSchema).test(
                    'issued-range',
                    'Date must be between 01/03/2013 and today',
                    (value) => {
                        if (!value) return true;
                        return isIssuedDateInRange(value);
                    }
                ),
            })
        )
        .min(1, 'Add at least one license'),
});
